import { useEffect, useState } from 'react';
import { ApiClientError, api, type PhotoAnswer } from '@/lib/api';

/**
 * Weak signal (Phase 3 plan 1 #7, step 9): door scans, proof photos, Delivered and Not delivered
 * are kept on the phone (IndexedDB) and sent in order when the signal is back. Every action has an
 * ID made on the phone, so the server counts a repeat once. A refusal from the server (not a
 * network problem) stops that action and shows the reason to the courier.
 */

export type OutboxType = 'DOOR_SCAN' | 'PHOTO' | 'DELIVER' | 'FAIL';

export interface OutboxItem {
  /** the action's ID; for a photo also its clientPhotoId */
  id: string;
  type: OutboxType;
  shipmentId: string;
  awb: string;
  createdAt: string;
  /** the request body; DELIVER / FAIL may name a photo still on the phone by its clientPhotoId */
  body: Record<string, unknown>;
  blob?: Blob;
  state: 'WAITING' | 'FAILED';
  error?: { code: string; message: string };
  tries: number;
}

const DB = 'blink-courier';
const OUTBOX = 'outbox';
const PHOTOS = 'photos';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(OUTBOX, { keyPath: 'id' });
      // clientPhotoId → the server's evidence ID, once the photo is sent
      req.result.createObjectStore(PHOTOS, { keyPath: 'clientPhotoId' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const req = fn(tx.objectStore(store));
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
  });
}

export async function outboxItems(): Promise<OutboxItem[]> {
  const all = await run<OutboxItem[]>(OUTBOX, 'readonly', (s) => s.getAll());
  return all.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

const put = (item: OutboxItem) => run(OUTBOX, 'readwrite', (s) => s.put(item));
const remove = (id: string) => run(OUTBOX, 'readwrite', (s) => s.delete(id));

// ------------------------------------------------------------------ change notifications

const listeners = new Set<() => void>();
function changed() {
  for (const l of listeners) l();
}

/** The actions still on the phone (waiting or refused), refreshed whenever the outbox changes. */
export function useOutbox(): OutboxItem[] {
  const [items, setItems] = useState<OutboxItem[]>([]);
  useEffect(() => {
    let live = true;
    const load = () => void outboxItems().then((x) => live && setItems(x)).catch(() => undefined);
    listeners.add(load);
    load();
    return () => {
      live = false;
      listeners.delete(load);
    };
  }, []);
  return items;
}

// ------------------------------------------------------------------ sending

/** No connection, or the server is down for a moment: try again later. Anything else is a refusal. */
export function isNetworkError(e: unknown) {
  return e instanceof ApiClientError && (e.status === 0 || e.status === 408 || e.status === 429 || e.status >= 500);
}

const results = new Map<string, unknown>();

async function send(item: OutboxItem): Promise<unknown> {
  const path = `/courier/parcels/${item.shipmentId}`;
  switch (item.type) {
    case 'DOOR_SCAN':
      return api(`${path}/door-scan`, { method: 'POST', body: item.body });
    case 'PHOTO': {
      const form = new FormData();
      for (const [k, v] of Object.entries(item.body)) if (v != null) form.append(k, String(v));
      form.append('clientPhotoId', item.id);
      form.append('file', item.blob!, 'photo.jpg');
      const answer = await api<PhotoAnswer>(`${path}/photo`, { method: 'POST', body: form });
      await run(PHOTOS, 'readwrite', (s) => s.put({ clientPhotoId: item.id, evidenceId: answer.id }));
      return answer;
    }
    case 'DELIVER':
    case 'FAIL': {
      // a photo taken without signal: use the ID the server gave it when it was sent
      const { photoClientId, photoField, ...body } = item.body as { photoClientId?: string; photoField?: string } & Record<string, unknown>;
      if (photoClientId && photoField) {
        const sent = await run<{ evidenceId: string } | undefined>(PHOTOS, 'readonly', (s) => s.get(photoClientId));
        if (!sent) throw new ApiClientError(409, 'CONFLICT', 'The photo was not sent');
        body[photoField] = sent.evidenceId;
      }
      return api(`${path}/${item.type === 'DELIVER' ? 'deliver' : 'fail'}`, { method: 'POST', body });
    }
  }
}

let flushing: Promise<void> | null = null;

/** Sends what is waiting, oldest first; stops at the first network problem. */
export function flushOutbox(): Promise<void> {
  flushing ??= (async () => {
    try {
      for (const item of await outboxItems()) {
        if (item.state !== 'WAITING') continue;
        try {
          results.set(item.id, await send(item));
          await remove(item.id);
        } catch (e) {
          // no signal, or signed out (the action waits until the courier signs in again)
          if (isNetworkError(e) || (e instanceof ApiClientError && e.status === 401)) {
            await put({ ...item, tries: item.tries + 1 });
            break;
          }
          const err = e instanceof ApiClientError ? e : new ApiClientError(500, 'INTERNAL', String(e));
          await put({ ...item, state: 'FAILED', tries: item.tries + 1, error: { code: err.code, message: err.message } });
        }
        changed();
      }
    } finally {
      flushing = null;
      changed();
    }
  })();
  return flushing;
}

export type SendResult<T> = { sent: true; answer: T } | { sent: false };

/**
 * Keeps the action on the phone, then tries to send everything waiting. Sent: the server's answer.
 * Still waiting (weak signal): { sent: false }. Refused by the server: the error is thrown and the
 * action is taken out, so the courier can correct it and try again.
 */
export async function sendOrKeep<T>(item: Omit<OutboxItem, 'createdAt' | 'state' | 'tries'>): Promise<SendResult<T>> {
  await put({ ...item, createdAt: new Date().toISOString(), state: 'WAITING', tries: 0 });
  changed();
  await flushOutbox();
  if (results.has(item.id)) {
    const answer = results.get(item.id) as T;
    results.delete(item.id);
    return { sent: true, answer };
  }
  const left = (await outboxItems()).find((x) => x.id === item.id);
  if (left?.state === 'FAILED') {
    await remove(item.id);
    changed();
    throw new ApiClientError(409, left.error!.code as ApiClientError['code'], left.error!.message);
  }
  return { sent: false };
}

/** The courier drops an action the server refused (it stays in the server's records only if it was sent). */
export async function discardOutboxItem(id: string) {
  await remove(id);
  changed();
}

let started = false;

/** Sends when the signal comes back, when the app is opened again, and every 20 seconds. */
export function startOutbox() {
  if (started) return;
  started = true;
  const tick = () => void flushOutbox().catch(() => undefined);
  window.addEventListener('online', tick);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && tick());
  setInterval(tick, 20_000);
  tick();
}
